import { ActionsCell } from '../../../Component/Common/Components/DataTable/ActionsCell';
import { DefaultCell } from '../../../Component/Common/Components/DataTable/DefaultCell';
import { Individual } from '../../../Component/Model/Dto/Individual';
import { DateFormatter } from '../../../Component/Model/helpers';
import { TableColumn } from '../../../Component/Model/Table/Table';
import { CurrencyFormatter } from '../../../Helpers/CurrencyFormatter';

interface IndividualTableRow {
    individualId: number,
    identificationNumber: string,
    employeeCode: string,
    scorecardName: string;
    scorecardType: string;
    firstName: string;
    lastName: string;
    fullName: ICustomCell,
    race: ICustomCell,
    gender: ICustomCell,
    disabled: ICustomCell,
    createdDate: string,
    createdBy: string,
    modifiedBy: string,
    fixedTotal: string;
    variableTotal: string;
    ctcTotal: string;
    reportingPeriodStartDate?: string;
    reportingPeriodEndDate?: string;
    actions?: any,
    style?: ICustomColor
}

interface ICustomColor {
    color: string;
    background: string;
}

interface ICustomCell {
    item: string;
    background: string;
}

export interface IIndividualSalariesTable {
    columns: Array<TableColumn>;
    rows: Array<IndividualTableRow>;
}

export function IndividualSalariesTableMapper(results: Individual.IndividualSalarySummaryDto[], onEditPeopleSalariesRow: (id: number, scorecardId: string, scorecardType: string) => void, isEditable: boolean, isEditableEE: boolean): IIndividualSalariesTable {
    const columns = [
        { Header: 'ID Number', accessor: 'identificationNumber', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Employee Code', accessor: 'employeeCode', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Disabled', accessor: 'disabled', id: "IsDisabled", Cell: ({ value }: any) => <DefaultCell value={value.item} backgroundColour={value.background} /> },
        { Header: 'Created Date', accessor: 'createdDate', id: "CreatedDateUtc", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Scorecard Name', accessor: 'scorecardName', id: 'ScorecardName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Scorecard Type', accessor: 'scorecardType', id: 'ScorecardType', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Fixed Total', accessor: 'fixedTotal', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Variable Total', accessor: 'variableTotal', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'CTC Total', accessor: 'ctcTotal', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Actions', accessor: 'actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true },
    ];

    if (results.length >= 0) {

        let backgroundColors: Record<string, string> = {};
        let currentColor: string = '#ffffff';

        const rows = results.map((item: Individual.IndividualSalarySummaryDto, index: number) => {
            if (!backgroundColors.hasOwnProperty(item.identificationNumber)) {
                backgroundColors[item.identificationNumber] = currentColor;
                currentColor = currentColor === '#ffffff' ? '#C1DCE0' : '#ffffff';
            }
            const discrepancyCells: string[] = [];

            if (index > 0) {
                const prevItem = results[index - 1];
                if (item.identificationNumber === prevItem.identificationNumber) {
                    if (item.fullName !== prevItem.fullName) {
                        discrepancyCells.push('FullName');
                    }
                    if (item.race !== prevItem.race) {
                        discrepancyCells.push('Race');
                    }
                    if (item.gender !== prevItem.gender) {
                        discrepancyCells.push('Gender');
                    }
                    if (item.disabled !== prevItem.disabled) {
                        discrepancyCells.push('IsDisabled');
                    }
                }
            }

            const rowStyle: ICustomColor = { color: '', background: backgroundColors[item.identificationNumber] };
            if (discrepancyCells.length > 0) {
                rowStyle.color = '#EF8164';
            }
            return {
                individualId: item.individualId,
                identificationNumber: item.identificationNumber,
                employeeCode: item.employeeCode,
                scorecardName: item.scorecardName,
                scorecardType: item.scorecardType,
                firstName: item.firstName,
                lastName: item.lastName,
                fullName: { item: item.fullName, background: discrepancyCells.includes('FullName') ? '#EF8164' : rowStyle.background },
                race: { item: item.race, background: discrepancyCells.includes('Race') ? '#EF8164' : rowStyle.background },
                gender: { item: item.gender, background: discrepancyCells.includes('Gender') ? '#EF8164' : rowStyle.background },
                disabled: { item: item.disabled ? 'Yes' : 'No', background: discrepancyCells.includes('IsDisabled') ? '#EF8164' : rowStyle.background },
                createdDate: DateFormatter(item.createdDate?.toString()),
                createdBy: item.createdBy ?? "Unspecified",
                modifiedBy: item.modifiedBy ?? "Unspecified",
                fixedTotal: CurrencyFormatter(item.fixedTotal),
                variableTotal: CurrencyFormatter(item.variableTotal),
                ctcTotal: CurrencyFormatter(item.ctcTotal),
                reportingPeriodStartDate: DateFormatter(item.reportingPeriodStartDate?.toString())  ?? "Unspecified",
                reportingPeriodEndDate: DateFormatter(item.reportingPeriodEndDate?.toString())  ?? "Unspecified",
                actions: [
                    (item.scorecardType === "Employment Equity" ? isEditableEE : isEditable) ?
                        {
                            key: index.toString(),
                            label: 'Edit Salaries',
                            action: () => {
                                onEditPeopleSalariesRow(item.individualId, item.scorecardId, item.scorecardType);
                            },
                            icon: {
                                color: 'info',
                                name: 'edit'
                            }
                        } :
                        {
                            key: index.toString(),
                            label: 'View Salaries',
                            action: () => {
                                onEditPeopleSalariesRow(item.individualId, item.scorecardId, item.scorecardType);
                            },
                            icon: {
                                color: 'info',
                                name: 'visibility'
                            }
                        }
                ],
                Style: { color: rowStyle.color, background: rowStyle.background }
            };
        });
        return { columns: columns, rows: rows };
    } else {
        return { columns: columns, rows: [] };
    }
}
